import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {InputSwitchComponent} from '../../input-switch/input-switch.component';

import {SiteConfigRule} from '../../../../models/site-config-rule';
import {SiteConfigParam} from '../../../../models/site-config-param';
import {FormsModule} from '@angular/forms';
import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {AuthService} from '../../../../services/auth/auth.service';
import {SiteConfigRuleService} from '../../../../services/site-config-rule/site-config-rule.service';
import {SiteConfigParamService} from '../../../../services/site-config-param/site-config-param.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {SiteConfigFormComponent} from '../site-config-form/site-config-form.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {ConsentModeFormComponent} from './consent-mode-form/consent-mode-form.component';
import {ConsentMode} from '../../../../types/consent-mode';

@Component({
  selector: 'app-site-config-element',
  imports: [
    InputSwitchComponent,
    FormsModule,
    ConfirmDirective
],
  templateUrl: './site-config-element.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './site-config-element.component.scss'
})
export class SiteConfigElementComponent implements OnInit {
  @Input() siteConfigRule: SiteConfigRule | null = null;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Output() deleted: EventEmitter<void> = new EventEmitter();
  @Output() updated: EventEmitter<void> = new EventEmitter();

  siteConfigParams: SiteConfigParam[] = [];

  constructor(
    private auth: AuthService,
    private loader: LoaderService,
    private dialog: DialogService,
    private siteConfigRuleService: SiteConfigRuleService,
    private siteConfigParamService: SiteConfigParamService,
  ) {
  }

  ngOnInit(): void {
    this.siteConfigParamService.getList().subscribe(siteConfigParams => {
      this.siteConfigParams = siteConfigParams;
    });
  }

  changeParam(siteConfigRule: SiteConfigRule, siteConfigParam: SiteConfigParam, state: Event): void {
    if (this.disabled) {
      return;
    }

    const target: any = state.target;

    if (siteConfigRule.id) {
      const checked = target?.checked ?? false;

      if (siteConfigParam.key === 'cst' && checked) {
        this.openConsentModeModal(siteConfigRule, siteConfigParam, target);
      } else {
        this.siteConfigRuleService.changeParamState(siteConfigRule.id, siteConfigParam, checked).subscribe(() => {
          siteConfigRule.params[siteConfigParam.key ?? ''] = checked;
        });
      }
    }
  }

  openConsentModeModal(siteConfigRule: SiteConfigRule, siteConfigParam: SiteConfigParam, target: any = null): void {
    const id = siteConfigRule.id;
    if (!id) {
      return;
    }

    const cst = siteConfigRule.params['cst'];

    this.dialog.openDialog(ConsentModeFormComponent, {data: cst}).subscribe((consentMode: ConsentMode) => {
      if (consentMode) {
        this.siteConfigRuleService.changeParamState(id, siteConfigParam, consentMode).subscribe(() => {
          siteConfigRule.params['cst'] = consentMode;
        });
      } else if (!cst && target) {
        target.checked = false;
      }
    });
  }

  isParamChecked(siteConfigRule: SiteConfigRule | null = null, siteConfigParam: SiteConfigParam | null = null): boolean {
    if (siteConfigRule && siteConfigParam && siteConfigParam.key) {
      return siteConfigRule.getParamValue(siteConfigParam.key) !== false;
    }

    return false;
  }

  updateRule(siteConfigRule: SiteConfigRule): void {
    if (this.disabled) {
      return;
    }

    this.dialog.openDialog(SiteConfigFormComponent, {
      data: {
        id: siteConfigRule?.id
      },
      backdrop: false
    }).subscribe(() => {
      this.updated.emit();
    });
  }

  deleteRule(siteConfigRule: SiteConfigRule): void {
    if (this.disabled) {
      return;
    }

    if (siteConfigRule.id) {
      this.loader.startLoading();
      this.siteConfigRuleService.delete(siteConfigRule.id).subscribe(() => {
        this.loader.stopLoading();
        this.deleted.emit();
      });
    }
  }
}
